import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, signal, DOCUMENT, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { MenuItem } from '../../../models/menu/menu-item';
import { CommonService } from '../../../services/application/common.service';
import { FaIconNamePipe } from '@cnp/pipes';
import { ChatButtonComponent } from '../chat/chat-button/chat-button.component';

// Ignore jitter and the iOS rubber-band bounce; only a deliberate scroll
// gesture should toggle the bar.
const SCROLL_DELTA_THRESHOLD = 8;
// Above this offset the bar is always visible, so the top of a page never
// starts with the nav hidden.
const ALWAYS_VISIBLE_OFFSET = 64;
/** Paired with the `body.has-bottom-nav` rule in styles.scss. */
const BODY_CLASS = 'has-bottom-nav';

@Component({
  selector: 'app-mobile-bottom-nav',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    FaIconComponent,
    FaIconNamePipe,
    ChatButtonComponent,
  ],
  templateUrl: './mobile-bottom-nav.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './mobile-bottom-nav.component.scss',
})
export class MobileBottomNavComponent implements OnInit, OnDestroy {
  private _commonService = inject(CommonService);
  private _document = inject<Document>(DOCUMENT);

  /** Route-driven destinations rendered before the chat button. */
  @Input() public items: MenuItem[] = [];
  @Input() public currentRoute: string;
  /** Mocked until the chatroom backend exists. */
  @Input() public chatUnreadCount = 0;
  /** Hidden for restricted users, whose overflow menu has nothing to show. */
  @Input() public showMenuButton = true;

  @Output() public itemSelected = new EventEmitter<MenuItem>();
  @Output() public chatClicked = new EventEmitter<void>();
  @Output() public menuClicked = new EventEmitter<void>();

  public isVisible = signal(true);

  private _lastScrollY = 0;
  private _ticking = false;

  public ngOnInit(): void {
    if (!this._commonService.isPlatformBrowser) {
      return;
    }

    this._document.body.classList.add(BODY_CLASS);
    this._lastScrollY = window.scrollY;
    window.addEventListener('scroll', this._onScroll, { passive: true });
  }

  public ngOnDestroy(): void {
    if (!this._commonService.isPlatformBrowser) {
      return;
    }

    this._document.body.classList.remove(BODY_CLASS);
    window.removeEventListener('scroll', this._onScroll);
  }

  public isItemActive(item: MenuItem): boolean {
    if (!this.currentRoute || !item.route) {
      return false;
    }

    return (
      this.currentRoute === item.route ||
      this.currentRoute.startsWith(item.route + '/')
    );
  }

  public onItemClick(item: MenuItem): void {
    this.itemSelected.emit(item);
  }

  public onChatClick(): void {
    this.chatClicked.emit();
  }

  public onMenuClick(): void {
    this.menuClicked.emit();
  }

  // Arrow property so add/removeEventListener share the same reference.
  private _onScroll = (): void => {
    if (this._ticking) {
      return;
    }

    this._ticking = true;
    // Reading scrollY inside rAF keeps the handler off the scroll critical
    // path -- the listener itself does no layout work.
    requestAnimationFrame(() => {
      this._updateVisibility();
      this._ticking = false;
    });
  };

  private _updateVisibility(): void {
    const currentScrollY = Math.max(window.scrollY, 0);
    const delta = currentScrollY - this._lastScrollY;

    if (Math.abs(delta) < SCROLL_DELTA_THRESHOLD) {
      return;
    }

    if (currentScrollY <= ALWAYS_VISIBLE_OFFSET) {
      this.isVisible.set(true);
    } else {
      this.isVisible.set(delta < 0);
    }

    this._lastScrollY = currentScrollY;
  }
}
